import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext.js";
import { api, ApiError } from "../../api/client.js";
import { TellYourFriendsCard } from "../../components/TellYourFriendsCard.js";
import type { CostEfficiencyResult, PersonaSummary, VideoHistoryRow } from "../../api/types.js";

interface OnboardingStatusResult {
  readonly canGenerateVideo: boolean;
  readonly licenseStatus: string | null;
  readonly planName: string | null;
  readonly generationsThisMonth: number | null;
  readonly monthlyGenerationLimit: number | null;
}

/**
 * PROCESSING fix (2026-10-01): the count and the list of in-progress videos come from the backend (`inProgressCount` / `inProgressVideos` — a real query over
 * EVERY non-terminal video of the tenant: draft, generating, ready_for_review, approved, upscaling, subtitle_processing), never from the limit=20 history page.
 */
/** How often the PROCESSING indicator re-reads the (cheap) in-progress endpoint while the Dashboard is open. */
export const DASHBOARD_IN_PROGRESS_POLL_MS = 12_000;

interface InProgressVideo {
  readonly id: string;
  readonly humanReadableName: string;
  readonly status: string;
}

interface StatusItem {
  readonly key: string;
  readonly label: string;
  /** Short value shown in the bar itself — omitted (never fabricated) when there's nothing useful to show inline. */
  readonly value?: ReactNode;
  /** Small count/indicator — only when genuinely relevant (e.g. something IS processing). */
  readonly badge?: ReactNode;
  /** The full detail shown in the on-click panel — the SAME data the old card's value+sub already showed, never less. */
  readonly title: string;
  readonly details: ReactNode;
}

function StatusBarButton({ item, onOpen }: { readonly item: StatusItem; readonly onOpen: (key: string) => void }) {
  return (
    <button type="button" className="status-bar-item" aria-haspopup="dialog" onClick={() => onOpen(item.key)}>
      <span className="status-bar-label">{item.label}</span>
      {item.value !== undefined && <span className="status-bar-value">{item.value}</span>}
      {item.badge !== undefined && <span className="status-bar-badge">{item.badge}</span>}
    </button>
  );
}

/**
 * FIX-007: the customer-facing landing surface — concise, tenant-scoped.
 * Deliberately reuses existing authorized endpoints only
 * (`onboarding-status`, `personas`, the history list, `cost-efficiency`)
 * — no platform-wide analytics, no Fanvue/Instagram/bot metrics, nothing
 * this tenant's own account couldn't already see elsewhere in GENERATE.
 * FIX-014 (2026-09): "Model" replaced with a truthful "Latest model" card
 * sourced from the latest video's own real canonical generation job
 * (never a recommended/default model), plus 3 new cost-efficiency cards.
 * 2026-09-28: the tall card grid became a compact single-row status bar
 * (same data, same source, nothing recomputed) — every item's full
 * detail (identical text to the old card) now opens on click via the
 * existing .modal-overlay pattern instead of always being on screen.
 */
export function DashboardTab({ tenantId }: { readonly tenantId: string }) {
  const { user } = useAuth();
  const [status, setStatus] = useState<OnboardingStatusResult | null>(null);
  const [personas, setPersonas] = useState<PersonaSummary[]>([]);
  const [videos, setVideos] = useState<VideoHistoryRow[]>([]);
  const [inProgressVideos, setInProgressVideos] = useState<InProgressVideo[]>([]);
  const [inProgressCount, setInProgressCount] = useState(0);
  const [costEfficiency, setCostEfficiency] = useState<CostEfficiencyResult | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [openKey, setOpenKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setLoading(true);
      setLoadError(null);
      try {
        const [statusResult, personaResult, historyResult, costEfficiencyResult] = await Promise.all([
          api.get<OnboardingStatusResult>(`/videos/${tenantId}/onboarding-status`).catch(() => null),
          api.get<{ personas: PersonaSummary[] }>(`/videos/${tenantId}/personas`),
          api.get<{ videos: VideoHistoryRow[]; inProgressCount?: number; inProgressVideos?: InProgressVideo[] }>(`/videos/${tenantId}?limit=20`),
          api.get<CostEfficiencyResult>(`/videos/${tenantId}/cost-efficiency`).catch(() => null),
        ]);
        if (cancelled) return;
        setStatus(statusResult);
        setPersonas(personaResult.personas);
        setVideos(historyResult.videos);
        setInProgressVideos(historyResult.inProgressVideos ?? []);
        setInProgressCount(historyResult.inProgressCount ?? 0);
        setCostEfficiency(costEfficiencyResult);
      } catch (error) {
        if (!cancelled) setLoadError(error instanceof ApiError ? error.message : "Failed to load Dashboard data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  // PROCESSING auto-refresh (2026-10-01): only the lightweight in-progress read is repeated (never History, personas, onboarding or cost-efficiency). It refreshes on a
  // timer, when the tab/window regains focus or becomes visible, and stops when the Dashboard unmounts or the tenant changes. A failed refresh keeps the last known
  // good value (never resets the count to 0) and is retried on the next tick.
  useEffect(() => {
    if (loading) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const refresh = async (): Promise<void> => {
      try {
        const result = await api.get<{ inProgressCount: number; inProgressVideos: InProgressVideo[] }>(`/videos/${tenantId}/in-progress`);
        if (cancelled) return;
        setInProgressCount(result.inProgressCount);
        setInProgressVideos(result.inProgressVideos);
      } catch {
        // keep the last known good state
      }
    };
    const schedule = (): void => {
      timer = setTimeout(() => {
        void (async () => {
          if (typeof document === "undefined" || document.visibilityState !== "hidden") await refresh();
          if (!cancelled) schedule();
        })();
      }, DASHBOARD_IN_PROGRESS_POLL_MS);
    };
    const onFocus = (): void => void refresh();
    const onVisible = (): void => {
      if (document.visibilityState === "visible") void refresh();
    };
    schedule();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [tenantId, loading]);

  if (loading) return <div className="empty-state">Loading…</div>;
  if (loadError) return <div className="error-banner">{loadError}</div>;

  const defaultPersona = personas.find((p) => p.isDefault === true) ?? personas[0] ?? null;
  const latest = videos[0] ?? null;

  const remainingThisMonth =
    status?.monthlyGenerationLimit !== null && status?.monthlyGenerationLimit !== undefined && status?.generationsThisMonth !== null
      ? `${Math.max(status.monthlyGenerationLimit - (status.generationsThisMonth ?? 0), 0)} remaining this month`
      : "No monthly limit on this plan";

  const items: StatusItem[] = [
    {
      key: "account",
      label: "Account",
      value: user?.emailVerified ? "Active" : "Action required",
      title: "Account status",
      details: (
        <div className="status-bar-detail-row">
          <div className="status-bar-detail-value">{user?.emailVerified ? "Active" : "Action required"}</div>
          <div className="status-bar-detail-sub">
            {user?.emailVerified ? user?.email : (
              <>
                Verify your email to unlock Generate — see <Link to="/account">Account</Link>
              </>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "plan",
      label: "Plan",
      value: status?.planName ?? "None",
      title: "Plan",
      details: (
        <div className="status-bar-detail-row">
          <div className="status-bar-detail-value">{status?.planName ?? "No plan assigned"}</div>
          <div className="status-bar-detail-sub">{status?.licenseStatus ?? "—"}</div>
        </div>
      ),
    },
    {
      key: "gen",
      label: "Gen",
      value: status?.generationsThisMonth ?? "—",
      title: "Generations this month",
      details: (
        <div className="status-bar-detail-row">
          <div className="status-bar-detail-value">
            {status?.generationsThisMonth ?? "—"}
            {status?.monthlyGenerationLimit !== null && status?.monthlyGenerationLimit !== undefined ? ` / ${status.monthlyGenerationLimit}` : ""}
          </div>
          <div className="status-bar-detail-sub">{remainingThisMonth}</div>
        </div>
      ),
    },
    {
      key: "model",
      label: "Model",
      value: latest?.modelId ?? (latest === null ? "No generations yet" : "Not recorded"),
      title: "Latest model",
      details: (
        <div className="status-bar-detail-row">
          <div className="status-bar-detail-value">{latest?.modelId ?? (latest === null ? "No generations yet" : "Not recorded")}</div>
          <div className="status-bar-detail-sub">{defaultPersona !== null ? `Persona: ${defaultPersona.displayName}` : <Link to="/generate/personas">Set up your Persona</Link>}</div>
        </div>
      ),
    },
    {
      key: "processing",
      label: "Processing",
      value: inProgressCount > 0 ? undefined : "—",
      badge: inProgressCount > 0 ? String(inProgressCount) : undefined,
      title: "Processing",
      details:
        inProgressVideos.length === 0 ? (
          <div className="status-bar-detail-row">
            <div className="status-bar-detail-value">Nothing in progress</div>
            <div className="status-bar-detail-sub">Start a new generation from Create</div>
          </div>
        ) : (
          <div className="status-bar-detail-row">
            {inProgressVideos.map((v) => (
              <div key={v.id} className="status-bar-detail-value">
                {v.humanReadableName} — {v.status.replace(/_/g, " ")}
              </div>
            ))}
          </div>
        ),
    },
    {
      key: "latest",
      label: "Latest",
      value: latest ? latest.humanReadableName : "No generations yet",
      title: "Latest generation",
      details: (
        <div className="status-bar-detail-row">
          <div className="status-bar-detail-value">{latest ? latest.humanReadableName : "No generations yet"}</div>
          <div className="status-bar-detail-sub">{latest ? latest.outcomeLabel : "Your finished work will show up here"}</div>
        </div>
      ),
    },
    {
      key: "cost",
      label: "Cost",
      value: costEfficiency?.averageCostPerVideoUsd !== null && costEfficiency?.averageCostPerVideoUsd !== undefined ? `$${costEfficiency.averageCostPerVideoUsd.toFixed(2)}` : "—",
      title: "Average cost / video",
      details: (
        <div className="status-bar-detail-row">
          <div className="status-bar-detail-value">
            {costEfficiency?.averageCostPerVideoUsd !== null && costEfficiency?.averageCostPerVideoUsd !== undefined ? `$${costEfficiency.averageCostPerVideoUsd.toFixed(2)}` : "—"}
          </div>
          <div className="status-bar-detail-sub">
            {costEfficiency !== null && costEfficiency.includedVideoCount > 0
              ? `Based on ${costEfficiency.includedVideoCount} finalized video${costEfficiency.includedVideoCount === 1 ? "" : "s"}`
              : "No finalized videos with reconciled cost yet"}
          </div>
        </div>
      ),
    },
    {
      key: "duration",
      label: "Duration",
      value: costEfficiency?.averageDurationSeconds !== null && costEfficiency?.averageDurationSeconds !== undefined ? `${costEfficiency.averageDurationSeconds.toFixed(1)}s` : "—",
      title: "Average duration",
      details: (
        <div className="status-bar-detail-row">
          <div className="status-bar-detail-value">
            {costEfficiency?.averageDurationSeconds !== null && costEfficiency?.averageDurationSeconds !== undefined ? `${costEfficiency.averageDurationSeconds.toFixed(1)}s` : "—"}
          </div>
          <div className="status-bar-detail-sub">Real duration of the finalized asset</div>
        </div>
      ),
    },
    {
      key: "costPerSecond",
      label: "Cost/Sec",
      value: costEfficiency?.averageCostPerSecondUsd !== null && costEfficiency?.averageCostPerSecondUsd !== undefined ? `$${costEfficiency.averageCostPerSecondUsd.toFixed(3)}` : "—",
      title: "Average cost / second",
      details: (
        <div className="status-bar-detail-row">
          <div className="status-bar-detail-value">
            {costEfficiency?.averageCostPerSecondUsd !== null && costEfficiency?.averageCostPerSecondUsd !== undefined ? `$${costEfficiency.averageCostPerSecondUsd.toFixed(3)}` : "—"}
          </div>
          <div className="status-bar-detail-sub">Aggregate tracked cost / aggregate tracked duration</div>
        </div>
      ),
    },
    {
      // FIX-012: same public /signup link, same zero-backend-call component, unchanged — only its presentation moves from a bottom card into this bar's on-click panel.
      key: "share",
      label: "Share",
      title: "Tell your friends about us",
      details: <TellYourFriendsCard />,
    },
  ];

  const openItem = items.find((item) => item.key === openKey) ?? null;

  return (
    <>
      <div className="status-bar">
        {items.map((item) => (
          <StatusBarButton key={item.key} item={item} onOpen={setOpenKey} />
        ))}
      </div>
      {openItem !== null && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={openItem.title}>
          <div className="modal-content card" style={{ maxWidth: 420 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>{openItem.title}</h3>
              <button onClick={() => setOpenKey(null)}>Close</button>
            </div>
            {openItem.details}
          </div>
        </div>
      )}
    </>
  );
}
